// Enhanced Infinite Loading for Featured Collection
class FeaturedCollectionInfiniteLoading {
  constructor() {
    this.init();
  }

  init() {
    // Wait for the featured collection component to be ready
    this.waitForElement('m-featured-collection').then((element) => {
      this.enhanceInfiniteLoading(element);
    });
  }

  waitForElement(selector) {
    return new Promise((resolve) => {
      if (document.querySelector(selector)) {
        return resolve(document.querySelector(selector));
      }

      const observer = new MutationObserver(() => {
        if (document.querySelector(selector)) {
          observer.disconnect();
          resolve(document.querySelector(selector));
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    });
  }

  enhanceInfiniteLoading(element) {
    // Check if infinite loading is enabled
    if (element.dataset.infiniteLoad !== 'true') {
      return;
    }

    // Set up enhanced scroll listener for infinite loading
    this.setupEnhancedScrollListener(element);
    
    // Set up enhanced load more button listener
    this.setupEnhancedLoadMoreButton(element);
  }

  setupEnhancedScrollListener(element) {
    let currentPage = 1;
    let isLoading = false;
    let canLoad = true;

    const handleScroll = () => {
      if (isLoading || !canLoad) {
        return;
      }

      const rect = element.getBoundingClientRect();
      const isNearBottom = rect.bottom <= window.innerHeight + 300; // Increased threshold for better UX

      if (isNearBottom) {
        isLoading = true;
        console.log(`Auto-loading page ${currentPage + 1} via scroll (page ${currentPage})`);
        this.loadMoreProductsDirectly(element, currentPage + 1).then((success) => {
          isLoading = false;
          if (success) {
            currentPage++;
            console.log(`Auto-loaded page ${currentPage}`);
            this.countProductsLoaded(element);
            
            // Check if all products are loaded
            if (this.checkIfAllProductsLoaded(element)) {
              canLoad = false;
              console.log('All products have been loaded, stopping infinite load');
            }
          } else {
            // No more products available
            canLoad = false;
            console.log('No more products available, stopping infinite load');
          }
        }).catch((error) => {
          isLoading = false;
          console.error('Error loading products:', error);
        });
      }
    };

    // Throttle scroll events for better performance
    let scrollTimeout;
    const throttledScroll = () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      scrollTimeout = setTimeout(handleScroll, 100);
    };

    window.addEventListener('scroll', throttledScroll);
    
    // Initial check
    handleScroll();
  }

  setupEnhancedLoadMoreButton(element) {
    // Hide the load more button permanently - we only want automatic scroll loading
    const loadMoreBtn = element.querySelector('[data-load-more]');
    if (loadMoreBtn) {
      // Hide the button immediately
      this.hideLoadMoreSection(element);
      console.log('Load more button hidden - using automatic scroll loading only');
    }
  }

  async triggerLoadMore(element, page) {
    // Trigger the existing load more functionality
    const loadMoreBtn = element.querySelector('[data-load-more]');
    if (loadMoreBtn) {
      // Check if button is visible and not disabled
      if (loadMoreBtn.style.display !== 'none' && !loadMoreBtn.disabled) {
        console.log(`Triggering load more for page ${page}`);
        
        // Hide the button and show loading state
        this.showLoadingState(loadMoreBtn);
        
        // Trigger the load more
        loadMoreBtn.click();
        
        // Wait for loading to complete
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Check if button is still available
        const stillAvailable = loadMoreBtn.style.display !== 'none' && !loadMoreBtn.disabled;
        console.log(`Load more triggered, button still available: ${stillAvailable}`);
        
        // If no more products, hide the entire button container
        if (!stillAvailable) {
          this.hideLoadMoreSection(element);
        } else {
          // Hide loading state
          this.hideLoadingState(loadMoreBtn);
        }
        
        return stillAvailable;
      } else {
        console.log('Load more button is not available (hidden or disabled)');
        this.hideLoadMoreSection(element);
      }
    } else {
      console.log('Load more button not found');
    }
    return false;
  }

  async loadMoreProductsDirectly(element, page) {
    // Load products directly using the section's built-in functionality
    const loadMoreBtn = element.querySelector('[data-load-more]');
    
    console.log(`Attempting to load page ${page}`);
    console.log(`Button found: ${!!loadMoreBtn}`);
    console.log(`Button visible: ${loadMoreBtn ? loadMoreBtn.style.display !== 'none' : false}`);
    console.log(`Button enabled: ${loadMoreBtn ? !loadMoreBtn.disabled : false}`);
    
    if (loadMoreBtn && loadMoreBtn.style.display !== 'none' && !loadMoreBtn.disabled) {
      console.log(`Loading page ${page} directly`);
      
      // Store initial product count
      const initialCount = this.countProductsLoaded(element);
      
      // Trigger the load more
      loadMoreBtn.click();
      
      // Wait for loading to complete
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // Check if more products are available
      const stillAvailable = loadMoreBtn.style.display !== 'none' && !loadMoreBtn.disabled;
      const finalCount = this.countProductsLoaded(element);
      
      console.log(`Direct load completed:`);
      console.log(`- Initial products: ${initialCount}`);
      console.log(`- Final products: ${finalCount}`);
      console.log(`- Products added: ${finalCount - initialCount}`);
      console.log(`- More products available: ${stillAvailable}`);
      
      // If no products were added, consider it failed
      if (finalCount === initialCount) {
        console.log('No products were added, considering load failed');
        return false;
      }
      
      return stillAvailable;
    } else {
      console.log('No more products available for direct loading');
      return false;
    }
  }

  // Add method to check if more products are available
  checkMoreProductsAvailable(element) {
    const loadMoreBtn = element.querySelector('[data-load-more]');
    const productsContainer = element.querySelector('[data-products-container]');
    
    if (!loadMoreBtn || !productsContainer) {
      return false;
    }

    // Check if button is visible (meaning more products are available)
    return loadMoreBtn.style.display !== 'none';
  }

  // Add method to count total products loaded
  countProductsLoaded(element) {
    const productsContainer = element.querySelector('[data-products-container]');
    if (productsContainer) {
      const productCards = productsContainer.querySelectorAll('.m-product-card');
      console.log(`Total products loaded: ${productCards.length}`);
      return productCards.length;
    }
    return 0;
  }

  // Method to get total products in collection
  getTotalProductsInCollection(element) {
    const loadMoreBtn = element.querySelector('[data-load-more]');
    if (loadMoreBtn) {
      // Try to get total from data attributes
      const totalProducts = loadMoreBtn.dataset.totalProducts || 
                           element.dataset.totalProducts ||
                           loadMoreBtn.getAttribute('data-total-products');
      
      if (totalProducts) {
        console.log(`Total products in collection: ${totalProducts}`);
        return parseInt(totalProducts);
      }
    }
    
    // Fallback: try to find product count in the section
    const productCountElement = element.querySelector('.m-product-count, [data-product-count]');
    if (productCountElement) {
      const countText = productCountElement.textContent;
      const match = countText.match(/(\d+)/);
      if (match) {
        console.log(`Total products from count element: ${match[1]}`);
        return parseInt(match[1]);
      }
    }
    
    console.log('Could not determine total products in collection');
    return null;
  }

  // Method to check if all products are loaded
  checkIfAllProductsLoaded(element) {
    const loadedCount = this.countProductsLoaded(element);
    const totalCount = this.getTotalProductsInCollection(element);
    
    if (totalCount !== null) {
      const percentage = Math.round((loadedCount / totalCount) * 100);
      console.log(`Products loaded: ${loadedCount}/${totalCount} (${percentage}%)`);
      
      if (loadedCount >= totalCount) {
        console.log('All products have been loaded!');
        return true;
      }
    }
    
    return false;
  }

  // Method to force load all available pages (for testing)
  async forceLoadAllPages(element) {
    let currentPage = 1;
    
    console.log(`Force loading all available pages`);
    
    while (true) {
      const success = await this.loadMoreProductsDirectly(element, currentPage + 1);
      if (success) {
        currentPage++;
        console.log(`Force loaded page ${currentPage}`);
        // Wait a bit between loads
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Check if all products are loaded
        if (this.checkIfAllProductsLoaded(element)) {
          console.log('All products have been loaded, stopping force load');
          break;
        }
      } else {
        console.log(`No more products available at page ${currentPage}`);
        break;
      }
    }
    
    console.log(`Force loading completed. Total pages loaded: ${currentPage}`);
    this.countProductsLoaded(element);
  }

  // Method to analyze the collection and show detailed info
  analyzeCollection(element) {
    console.log('=== COLLECTION ANALYSIS ===');
    this.countProductsLoaded(element);
    this.getTotalProductsInCollection(element);
    this.checkIfAllProductsLoaded(element);
    
    // Check button status
    const loadMoreBtn = element.querySelector('[data-load-more]');
    if (loadMoreBtn) {
      console.log('Load more button status:');
      console.log(`- Found: ${!!loadMoreBtn}`);
      console.log(`- Visible: ${loadMoreBtn.style.display !== 'none'}`);
      console.log(`- Enabled: ${!loadMoreBtn.disabled}`);
      console.log(`- Classes: ${loadMoreBtn.className}`);
      console.log(`- Data attributes:`, loadMoreBtn.dataset);
    }
    
    console.log('=== END ANALYSIS ===');
  }



  // Show loading state - hide button text and show spinner
  showLoadingState(loadMoreBtn) {
    if (loadMoreBtn) {
      // Hide the button text
      const buttonText = loadMoreBtn.querySelector('span:not(.m-spinner-icon)');
      if (buttonText) {
        buttonText.style.display = 'none';
      }
      
      // Show the spinner
      const spinner = loadMoreBtn.querySelector('.m-spinner-icon');
      if (spinner) {
        spinner.style.display = 'inline-block';
      }
      
      // Disable the button
      loadMoreBtn.disabled = true;
      loadMoreBtn.classList.add('m-spinner-loading');
    }
  }

  // Hide loading state - show button text and hide spinner
  hideLoadingState(loadMoreBtn) {
    if (loadMoreBtn) {
      // Show the button text
      const buttonText = loadMoreBtn.querySelector('span:not(.m-spinner-icon)');
      if (buttonText) {
        buttonText.style.display = 'inline';
      }
      
      // Hide the spinner
      const spinner = loadMoreBtn.querySelector('.m-spinner-icon');
      if (spinner) {
        spinner.style.display = 'none';
      }
      
      // Enable the button
      loadMoreBtn.disabled = false;
      loadMoreBtn.classList.remove('m-spinner-loading');
    }
  }

  // Hide the entire load more section when no more products
  hideLoadMoreSection(element) {
    const loadMoreBtn = element.querySelector('[data-load-more]');
    const loadMoreContainer = loadMoreBtn ? loadMoreBtn.closest('.m-featured-collection__button') : null;
    
    if (loadMoreContainer) {
      // Hide the entire button container
      loadMoreContainer.style.display = 'none';
      console.log('No more products available - hiding load more section');
    } else if (loadMoreBtn) {
      // Fallback: hide just the button
      loadMoreBtn.style.display = 'none';
      console.log('No more products available - hiding load more button');
    }
  }
}

// Initialize enhanced infinite loading when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new FeaturedCollectionInfiniteLoading();
});

// Also initialize when Shopify's section:load event fires
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('m-featured-collection')) {
    new FeaturedCollectionInfiniteLoading();
  }
});
